import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  deleteCadStep,
  getCadDetails,
  getCadGlbUrl,
  getCadStatus,
  searchSimilarParts,
  uploadCadStep,
  type CadDetails,
  type CadGlbUrl,
  type CadSearchResult,
  type CadStatus,
  type CadUploadResult,
} from "@/api/cad";
import { parseGlbVolumeMm3 } from "@/utils/glb-volume";

export const CAD_KEY = ["cad3d"] as const;

const POLL_INTERVAL_MS = 2000;

// Poll status while the analysis is still running; stop once terminal.
export function useCadStatus(
  applicationId: string | null | undefined,
  enabled = true,
) {
  return useQuery<CadStatus>({
    queryKey: [...CAD_KEY, "status", applicationId],
    queryFn: () => getCadStatus(applicationId as string),
    enabled: !!applicationId && enabled,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "pending" || status === "running" ? POLL_INTERVAL_MS : false;
    },
    // 404 (no upload yet) is expected before the first upload — don't hammer retries.
    retry: false,
  });
}

export function useUploadCadStep() {
  const queryClient = useQueryClient();
  return useMutation<CadUploadResult, unknown, { applicationId: string; file: File }>({
    mutationFn: ({ applicationId, file }) => uploadCadStep(applicationId, file),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [...CAD_KEY, "status", data.applicationId],
      });
    },
  });
}

export function useDeleteCadStep() {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, { applicationId: string }>({
    mutationFn: ({ applicationId }) => deleteCadStep(applicationId),
    onSuccess: (_data, { applicationId }) => {
      // After a delete the status / glb / details endpoints return 404
      // (svc-09 drops the application_cad3d_current row). `invalidateQueries`
      // refetches, but React Query KEEPS the last successful data on a 404 error
      // (stale-on-error) — so the deleted file lingered in the panel until a full
      // page reload wiped the cache. Remove the cached entries instead: active
      // observers re-create from a clean slate (data === undefined) and the panel
      // reflects "no upload" immediately. ("search" cleared too — it referenced
      // the now-deleted part.)
      for (const sub of ["status", "glb", "details", "search"]) {
        queryClient.removeQueries({ queryKey: [...CAD_KEY, sub, applicationId] });
      }
    },
  });
}

export function useCadGlbUrl(
  applicationId: string | null | undefined,
  enabled: boolean,
) {
  return useQuery<CadGlbUrl>({
    queryKey: [...CAD_KEY, "glb", applicationId],
    queryFn: () => getCadGlbUrl(applicationId as string),
    enabled: !!applicationId && enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

// Geometry properties for the stepcheck properties panel.
export function useCadDetails(
  applicationId: string | null | undefined,
  enabled: boolean,
) {
  return useQuery<CadDetails>({
    queryKey: [...CAD_KEY, "details", applicationId],
    queryFn: () => getCadDetails(applicationId as string),
    enabled: !!applicationId && enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

// Estimated weight: fetch the GLB bytes (same presigned URL the viewer uses) and
// compute the enclosed volume client-side, exactly like the POC stepcheck. Returns
// null when the mesh isn't watertight enough, or when the fetch is blocked (CORS).
export function useCadVolume(url: string | null | undefined) {
  return useQuery<number | null>({
    queryKey: [...CAD_KEY, "volume", url],
    queryFn: async () => {
      const res = await fetch(url as string);
      if (!res.ok) throw new Error(`GLB fetch failed (${res.status})`);
      return parseGlbVolumeMm3(await res.arrayBuffer());
    },
    enabled: !!url,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

// Similar-parts search (internal users only — 403 otherwise).
export function useCadSearch(
  applicationId: string | null | undefined,
  enabled: boolean,
) {
  return useQuery<CadSearchResult>({
    queryKey: [...CAD_KEY, "search", applicationId],
    queryFn: () => searchSimilarParts(applicationId as string),
    enabled: !!applicationId && enabled,
    staleTime: 60 * 1000,
    retry: false,
  });
}
